import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, KeyboardEventHandler } from 'react';
import {
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    useDisclosure,
} from '@chakra-ui/react';
import debounce from 'lodash-es/debounce';
import { useLocation } from 'react-router-dom';
import {
    shouldQueryBloomreach,
    useGetBloomreachSearch,
    useGetSuggestedBrands,
} from '../../../../bff-api/bloomreach-recommendation/useBloomreachSearch';
import { useGTM, useNavigateTo } from '../../../../hooks';
import { useIsMobile } from '../../../../hooks/use-is-mobile/useIsMobile';
import { useBuildURL } from '../../../../hooks/useBuildURL';
import { PageUrl } from '../../../../pages/utils/page-url';
import { useCustomPreferenceOrDefault } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { isHydrated } from '../../../../utils/utils';
import { SearchInput } from './SearchInput';
import { MenuSearchResults } from './menu-search-results';
import { getMobilePopoverHeight } from './utils';

interface Props {
    placeholder?: string;
    forceReloadOnSearch?: boolean;
    headerRef: React.RefObject<HTMLElement>;
    showSuggestions?: boolean;
}

export const MenuSearch: React.FC<Props> = ({
    placeholder,
    forceReloadOnSearch,
    headerRef,
    showSuggestions = true,
}) => {
    const { onSearch } = useGTM();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const location = useLocation();
    const navigateTo = useNavigateTo();
    const { buildSearchProductURL } = useBuildURL();
    const isMobile = useIsMobile();

    // Get the debounce time from custom preferences (default: 300ms)
    const searchDebounce = useCustomPreferenceOrDefault(CustomPreference.SEARCH_DEBOUNCE, 300);

    // Search term state: immediate updates for responsive UI
    const [searchTerm, setSearchTerm] = useState('');

    // Debounced search term: updates after user stops typing, used for API calls
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Bloomreach parameters for search API
    const [bloomreachParams, setBloomreachParams] = useState<{ url: string; refUrl: string }>({
        url: '',
        refUrl: '',
    });

    // Fetch search suggestions from Bloomreach
    const { data, isLoading } = useGetBloomreachSearch({
        ...bloomreachParams,
        q: debouncedSearchTerm,
        pageSize: 4,
    });

    // Fetch brand suggestions (only when 3+ characters)
    const brands = useGetSuggestedBrands({
        searchTerm: debouncedSearchTerm,
    });

    // Create debounced function to update search term for API calls
    // Only triggers after user stops typing for the configured debounce time
    const updateDebouncedSearchTerm = useMemo(
        () => debounce((value: string) => setDebouncedSearchTerm(value), searchDebounce),
        [searchDebounce],
    );

    // Cleanup debounced function on unmount to prevent memory leaks
    useEffect(
        () => () => {
            if (updateDebouncedSearchTerm.cancel) {
                updateDebouncedSearchTerm.cancel();
            }
        },
        [updateDebouncedSearchTerm],
    );

    /**
     * Resets search state and closes the suggestions popover
     * Used when user clicks a result, clears search, or presses Escape
     */
    const resetSearchAndClose = (): void => {
        onClose();
        setSearchTerm('');
        setDebouncedSearchTerm('');
    };

    /**
     * Handles search input changes
     * Updates search term immediately for UI, triggers debounced update for API calls
     */
    const handleOnSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        setSearchTerm(value);

        if (!value) {
            // Clear immediately if input is empty
            resetSearchAndClose();
            updateDebouncedSearchTerm.cancel();
        } else {
            // Trigger debounced update for API calls (will fire after user stops typing)
            updateDebouncedSearchTerm(value);
        }
    };

    /**
     * Handles search submission (Enter key or search button click)
     * Tracks in GTM and navigates to search results page
     */
    const onSubmitSearch = (): void => {
        if (!searchTerm || searchTerm.trim() === '') {
            return;
        }

        // Track search in Google Tag Manager
        onSearch(searchTerm);

        resetSearchAndClose();

        // Navigate to search page or trigger search event if already on search page
        if (location.pathname.startsWith(PageUrl.SEARCH) && !forceReloadOnSearch) {
            dispatchEvent(new CustomEvent('slt-search', { detail: searchTerm }));
        } else {
            navigateTo(buildSearchProductURL(searchTerm));
        }
    };

    /**
     * Closes the popover when user clicks outside the search component
     */
    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            const target = event.target as Element;
            if (isOpen && !target.closest('[data-testid="header.menu.search.results"]')) {
                onClose();
            }
        },
        [isOpen, onClose],
    );

    /**
     * Handles clearing the search input
     * Cancels any pending debounced updates and resets all search state
     */
    const onClearSearch = (): void => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        updateDebouncedSearchTerm.cancel();
        onClose();
    };

    /**
     * Handles keyboard events in the search input
     * - Escape: clears search and closes popover
     * - Enter: submits search
     * - Other keys: opens popover if search term is valid (3+ chars)
     */
    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e): void => {
        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            resetSearchAndClose();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            onSubmitSearch();
        } else if (shouldQueryBloomreach(searchTerm) && !isLoading) {
            onOpen();
        }
    };

    // Close popover on scroll (desktop only)
    useEffect(() => {
        const handleScroll = (): void => {
            if (!isMobile) {
                onClose();
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobile, onClose]);

    // Open popover when search results are ready and search term is valid (3+ chars)
    useEffect(() => {
        if (!isLoading && shouldQueryBloomreach(debouncedSearchTerm)) {
            onOpen();
        }
    }, [debouncedSearchTerm, isLoading, onOpen]);

    // Track search in GTM only when debounced search term updates and has 3+ characters
    useEffect(() => {
        if (shouldQueryBloomreach(debouncedSearchTerm)) {
            onSearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm, onSearch]);

    // Initialize Bloomreach params with current URL on mount
    useEffect(() => {
        if (isHydrated() && window.document.URL) {
            setBloomreachParams({
                url: window.document.URL,
                refUrl: window.document.referrer || '',
            });
        }
    }, []);

    // Handle clicks outside the search component to close popover
    useEffect(() => {
        document.removeEventListener('click', handleClickOutside);

        document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [handleClickOutside]);

    const popoverHeight = getMobilePopoverHeight(headerRef, !!isMobile);

    return showSuggestions ? (
        <Popover
            variant="slt-outline"
            isOpen={isOpen && showSuggestions}
            onClose={onClose}
            closeDelay={0}
            autoFocus={false}
        >
            <PopoverTrigger>
                <div className="w-full" data-testid="menu-search-container">
                    <SearchInput
                        searchTerm={searchTerm}
                        placeholder={placeholder}
                        showSuggestions={showSuggestions}
                        onSearch={handleOnSearch}
                        onKeyDown={onKeyDown}
                        onSubmitSearch={onSubmitSearch}
                        onClearSearch={onClearSearch}
                    />
                </div>
            </PopoverTrigger>
            <Portal>
                <PopoverContent padding={{ md: 4 }}>
                    <PopoverBody maxHeight={popoverHeight} height={popoverHeight}>
                        <MenuSearchResults
                            searchTerm={debouncedSearchTerm}
                            phrases={data?.phrases ?? []}
                            categories={data?.categories ?? []}
                            brands={brands?.data?.brands ?? []}
                            hardgood={data?.hardgood ?? []}
                            culinary={data?.culinary ?? []}
                            recipe={data?.recipe ?? []}
                            onClickResult={resetSearchAndClose}
                            onClosePopover={onClose}
                        />
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    ) : (
        <SearchInput
            searchTerm={searchTerm}
            placeholder={placeholder}
            showSuggestions={showSuggestions}
            onSearch={handleOnSearch}
            onKeyDown={onKeyDown}
            onSubmitSearch={onSubmitSearch}
            onClearSearch={onClearSearch}
        />
    );
};

MenuSearch.defaultProps = {
    placeholder: undefined,
    forceReloadOnSearch: false,
    showSuggestions: true,
};
